<script setup lang="ts">
  import { computed, ref, watch, defineComponent, h, provide } from 'vue'
  import draggable from 'vuedraggable'
  import {
    CloudUpload,
    FileIcon,
    FileTextIcon,
    Grip,
    MoreHorizontal,
    Plus,
    Trash2
  } from 'lucide-vue-next'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useUserStore } from '@/store/userStore'
  import type { CustomerCase as Case, CaseDashboardWidgetsEnum } from '@/types/generated'
  import { UsersService } from '@/types/generated/services.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import AddWidgetsButton from '@/components/app/cases/dashboard-widgets/addWidgetsButton.vue'

  // Import our custom widget components
  import ClientDataWidget from '@/components/app/cases/dashboard-widgets/ClientDataWidget.vue'
  import CaseStatusWidget from '@/components/app/cases/dashboard-widgets/CaseStatusWidget.vue'
  import CaseDataWidget from '@/components/app/cases/dashboard-widgets/CaseDataWidget.vue'
  import AppointmentsWidget from '@/components/app/cases/dashboard-widgets/AppointmentsWidget.vue'
  import DocumentsWidget from '@/components/app/cases/dashboard-widgets/DocumentsWidget.vue'
  import NotesWidget from '@/components/app/cases/dashboard-widgets/NotesWidget.vue'
  import OpponentDataWidget from '@/components/app/cases/dashboard-widgets/OpponentDataWidget.vue'
  import LawSpecificDataWidget from '@/components/app/cases/dashboard-widgets/LawSpecificDataWidget.vue'
  import AiChatWidget from '@/components/app/cases/dashboard-widgets/AiChatWidget.vue'
  import EntitiesWidget from '@/components/app/cases/dashboard-widgets/EntitiesWidget.vue'
  import GeneratedDocumentsWidget from '@/components/app/cases/dashboard-widgets/GeneratedDocumentsWidget.vue'
  import EmailsWidget from '@/components/app/cases/dashboard-widgets/EmailsWidget.vue'
  import ActivitiesWidget from '@/components/app/cases/dashboard-widgets/ActivitiesWidget.vue'

  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'

  import { Button } from '@/components/ui/button'
  const { t } = useTranslation({
    en: {
      title: 'Inquiries',
      newInquiry: 'New Inquiry',
      legalField: 'Legal Field',
      caseWorker: 'Case Worker',
      status: 'Status',
      timeframe: 'Timeframe',
      name: 'Name',
      legalFieldTheme: 'Legal Field/Theme',
      documents: 'Documents',
      receivedAt: 'Received at',
      notAssigned: 'Not assigned',
      newInquiryStatus: 'New Inquiry',
      inProgress: 'In Progress',
      rejected: 'Rejected',
      showMore: 'Show more',
      delete: 'Delete',
      deleteConfirmation: 'Are you sure you want to delete this case?',
      cancel: 'Cancel',
      allLegalFields: 'All Legal Fields',
      allCaseWorkers: 'All Case Workers',
      allStatuses: 'All Status',
      selectCaseWorkers: 'Select Case Workers',
      caseWorkers: 'Case Workers',
      caseWorkerUpdated: 'Case workers updated successfully',
      caseWorkerUpdateError: 'Failed to update case workers',
      integrations: {
        actaport: 'Actaport',
        advoware: 'AdvoWare',
        advolux: 'Advolux',
        raMicro: 'RA-MICRO',
        kleos: 'Kleos',
        jurNodes: 'JurNodes'
      },
      removeWidget: 'Remove widget',
      individualDashboard: 'Indivisual Dashboard'
    },
    de: {
      title: 'Anfragen',
      newInquiry: 'Neue Anfrage anlegen',
      legalField: 'Rechtsgebiet',
      caseWorker: 'Sachbearbeiter',
      status: 'Status',
      timeframe: 'Zeitraum',
      name: 'Name',
      legalFieldTheme: 'Rechtsgebiet/Thema',
      documents: 'Dokumente',
      receivedAt: 'Eingegangen am',
      notAssigned: 'Nicht zugewiesen',
      newInquiryStatus: 'Neue Anfrage',
      inProgress: 'In Bearbeitung',
      rejected: 'Abgelehnt',
      showMore: 'Mehr anzeigen',
      delete: 'Löschen',
      deleteConfirmation: 'Sind Sie sicher, dass Sie diesen Fall löschen möchten?',
      cancel: 'Abbrechen',
      allLegalFields: 'Alle Rechtsgebiete',
      allCaseWorkers: 'Alle Sachbearbeiter',
      allStatuses: 'Alle Status',
      selectCaseWorkers: 'Sachbearbeiter auswählen',
      caseWorkers: 'Sachbearbeiter',
      caseWorkerUpdated: 'Sachbearbeiter erfolgreich aktualisiert',
      caseWorkerUpdateError: 'Fehler beim Aktualisieren der Sachbearbeiter',
      integrations: {
        actaport: 'Actaport',
        advoware: 'AdvoWare',
        advolux: 'Advolux',
        raMicro: 'RA-MICRO',
        kleos: 'Kleos',
        jurNodes: 'JurNodes'
      },
      removeWidget: 'Widget entfernen',
      individualDashboard: 'Individuelles Dashboard'
    }
  })

  // Store setup
  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  const user = computed(() => userStore.user)

  // Loading state
  const isUpdatingWidgets = ref(false)

  // Form fields
  const selectedLegalField = ref('')
  const selectedCaseWorker = ref('')
  const selectedLegalTopic = ref('')
  const legalFieldOptions = ref([])
  const caseWorkerOptions = ref([])
  const legalTopicOptions = ref([])

  const dragStart = ref(false)

  interface WidgetAction {
    label: string
    icon?: any // Component reference
    handler: () => void // Direct function reference instead of string
  }

  interface WidgetDefinition {
    component: any
    actions?: WidgetAction
    showMenu?: boolean // Controls whether to show the dropdown menu
  }

  const widgetDefinitions: Record<string, WidgetDefinition> = {
    CLIENT_DATA: { component: ClientDataWidget },
    CURRENT_STATE: { component: CaseStatusWidget },
    ACTIVITIES: { component: ActivitiesWidget },
    APPOINTMENTS: {
      component: AppointmentsWidget,
      actions: {
        label: 'Neuer Termin',
        icon: Plus,
        handler: () => {}
      }
    },
    AI_CHAT: {
      component: AiChatWidget,
      showMenu: true
    },
    LAW_SPECIFIC_DATA: {
      component: LawSpecificDataWidget
    },
    DOCUMENTS: {
      component: DocumentsWidget,
      actions: {
        label: 'Dokumente hochladen',
        icon: CloudUpload,
        handler: () => {}
      }
    },
    GENERATED_DOCUMENTS: {
      component: GeneratedDocumentsWidget,
      actions: {
        label: 'Dokumente generieren',
        icon: FileTextIcon,
        handler: () => {}
      },
      showMenu: true
    },
    EMAILS: {
      component: EmailsWidget,
      actions: {
        label: 'Neue Email',
        icon: Plus,
        handler: () => {}
      }
    },
    ENTITIES: {
      component: EntitiesWidget,
      showMenu: true
    },
    NOTES: {
      component: NotesWidget
    },
    OPPONENT_DATA: {
      component: OpponentDataWidget
    },
    CASE_DATA: {
      component: CaseDataWidget,
      showMenu: false
    }
  }

  const widgetActionHandlers = ref<Record<string, () => void>>({})

  // Widget management
  const dashboardWidgets = computed({
    get: () => {
      return (
        user.value?.case_dashboard_widgets.map((widgetType) => {
          const widgetDef = widgetDefinitions[widgetType] || {
            component: defineComponent({ template: `<div>${widgetType} Widget</div>` })
          }

          return {
            type: widgetType,
            component: widgetDef.component,
            title: getWidgetTitle(widgetType),
            id: `widget-${widgetType}`,
            isAI: widgetType === 'AI_CHAT',
            // Add actions if defined
            actions: widgetDef.actions || null,
            showMenu: widgetDef.showMenu !== false // Default to true if not specified
          }
        }) || []
      )
    },
    set: async (newValue) => {
      if (!user.value?.uuid) return

      isUpdatingWidgets.value = true
      try {
        const newOrder = newValue.map((widget) => widget.type)
        if (userStore.user) {
          userStore.user.case_dashboard_widgets = newOrder
        }
        await UsersService.usersPartialUpdate({
          uuid: user.value.uuid,
          requestBody: {
            case_dashboard_widgets: newOrder
          }
        })
      } catch (error) {
        console.error('Failed to update widget order:', error)
      } finally {
        isUpdatingWidgets.value = false
      }
    }
  })

  // Function to handle drag start
  const onDragStart = () => {
    dragStart.value = true
    document.body.style.cursor = 'grabbing'
  }

  // Function to handle drag end
  const onDragEnd = () => {
    dragStart.value = false
    document.body.style.cursor = ''
  }

  // Widget title mapping
  const getWidgetTitle = (widgetType: CaseDashboardWidgetsEnum): string => {
    const titles: Record<CaseDashboardWidgetsEnum, string> = {
      CLIENT_DATA: 'Mandant Daten',
      CURRENT_STATE: 'Aktueller Stand der Anfrage',
      OPPONENT_DATA: 'Gegner Daten',
      ACTIVITIES: 'Letzte Aktivität',
      EMAILS: 'E-Mails',
      APPOINTMENTS: 'Termine',
      AI_CHAT: 'KI Chat',
      LAW_SPECIFIC_DATA: 'Rechtsspezifische Daten',
      DOCUMENTS: 'Dokumente',
      GENERATED_DOCUMENTS: 'Generierte Dokumente',
      ENTITIES: 'Beteiligte',
      NOTES: 'Notizen',
      CASE_DATA: 'Anfrage Daten'
    }
    return titles[widgetType] || widgetType
  }

  // Watchers
  watch(
    () => caseRecord.value?.org,
    (org) => {
      if (org) {
        legalFieldOptions.value = (org.law_fields || []).map((field) => ({
          value: field,
          title: field
        }))
        caseWorkerOptions.value = (org.users || []).map((user) => ({
          value: user.uuid,
          title: `${user.first_name} ${user.last_name}`
        }))
      }
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value,
    (caseRecord) => {
      if (caseRecord) {
        if (caseRecord.data?.law?.field) {
          selectedLegalField.value = caseRecord.data.law.field
        }
        if (caseRecord.data?.law?.topic) {
          selectedLegalTopic.value = caseRecord.data.law.topic
        }
        if (caseRecord.clerk) {
          selectedCaseWorker.value = caseRecord.clerk.uuid
        }
      }
    },
    { immediate: true }
  )

  // Method to handle widget action clicks
  const handleWidgetAction = (widgetType: string) => {
    if (widgetActionHandlers.value[widgetType]) {
      widgetActionHandlers.value[widgetType]()
    }
  }

  // Method to register a widget action handler
  const registerWidgetActionHandler = (widgetType: string, handler: () => void) => {
    widgetActionHandlers.value[widgetType] = handler
  }

  const removeWidget = async (widgetType: string) => {
    if (!user.value?.uuid) return

    isUpdatingWidgets.value = true
    try {
      // Create a new array without the removed widget
      const updatedWidgets = (user.value.case_dashboard_widgets || []).filter(
        (widget) => widget !== widgetType
      ) as CaseDashboardWidgetsEnum[]

      // Update locally
      if (userStore.user) {
        userStore.user.case_dashboard_widgets = updatedWidgets
      }

      // Update on the server
      await UsersService.usersPartialUpdate({
        uuid: user.value.uuid,
        requestBody: {
          case_dashboard_widgets: updatedWidgets
        }
      })
    } catch (error) {
      console.error('Failed to remove widget:', error)
    } finally {
      isUpdatingWidgets.value = false
    }
  }

  // Provide the registration method to child components
  provide('registerWidgetActionHandler', registerWidgetActionHandler)
</script>

<template>
  <div class="relative">
    <!-- Loading overlay -->
    <div
      v-if="isUpdatingWidgets"
      class="absolute inset-0 z-50 flex items-center justify-center bg-white/50"
    >
      <div class="flex items-center space-x-2">
        <div
          class="h-4 w-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
        ></div>
        <span class="text-sm text-gray-600">Updating widgets...</span>
      </div>
    </div>

    <div class="mb-4 font-bold">
      <div class="flex items-center justify-between text-lg font-medium">
        <div>{{ t('individualDashboard') }}</div>
        <AddWidgetsButton />
      </div>
    </div>

    <!-- Dashboard Widgets -->
    <draggable
      v-model="dashboardWidgets"
      :group="{ name: 'widgets' }"
      item-key="id"
      class="mt-4 columns-1 gap-4 sm:columns-2"
      handle=".widget-drag-handle"
      :animation="200"
      ghost-class="widget-ghost"
      drag-class="widget-drag"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <div
          class="relative mb-4 break-inside-avoid-column rounded-xl border border-gray-200 bg-white shadow-sm
            transition-transform duration-200"
        >
          <!-- Widget Header -->
          <div class="grid grid-cols-7 items-center border-gray-100 px-5 py-3">
            <!-- Left section with title -->
            <div class="col-span-3 flex items-center gap-2">
              <h3 class="truncate whitespace-nowrap text-base font-medium text-gray-800">
                {{ element.title }}
              </h3>
              <div
                v-if="element.isAI"
                class="flex h-6 w-7 items-center justify-center rounded bg-purple-100 text-xs font-medium text-purple-800"
              >
                KI
              </div>
            </div>

            <!-- Center section with drag handle -->
            <div class="group flex justify-center">
              <div
                class="widget-drag-handle invisible flex cursor-grab items-center justify-center rounded p-1 text-gray-400
                  hover:bg-gray-100 hover:text-gray-600 group-hover:visible"
                :class="{ 'cursor-grabbing': dragStart }"
              >
                <Grip class="h-5 w-5" />
              </div>
            </div>

            <!-- Right section with actions -->
            <div class="col-span-3 flex justify-end">
              <Button
                v-if="element.actions?.label"
                variant="secondary"
                class="mr-2 flex h-8 items-center gap-1 overflow-hidden"
                @click="handleWidgetAction(element.type)"
              >
                <component
                  :is="element.actions.icon"
                  v-if="element.actions.icon"
                  class="mr-1 h-4 w-4"
                />
                <span
                  v-else
                  class="text-lg font-normal"
                >
                  +
                </span>
                <div class="truncate">
                  {{ element.actions.label }}
                </div>
              </Button>
              <!-- Widget Menu Dropdown -->
              <DropdownMenu v-if="element.showMenu">
                <DropdownMenuTrigger
                  as-child
                  class="rounded border border-gray-300 p-1 text-black hover:bg-gray-100 hover:text-gray-600"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                  >
                    <MoreHorizontal class="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent :align="'start'">
                  <DropdownMenuItem @select="removeWidget(element.type)">
                    <div class="flex items-center gap-2 text-destructive">
                      <Trash2 class="h-4 w-4" />
                      <span>{{ t('removeWidget') }}</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <!-- Widget Content -->
          <div class="widget-content px-4 pb-4">
            <component :is="element.component" />
          </div>
        </div>
      </template>
    </draggable>
  </div>
  <!-- <EntityCard /> -->
</template>

<style scoped>
  /* Base styles for the drag handle */
  .widget-drag-handle {
    touch-action: none;
    transition: all 0.2s ease;
  }

  /* Ghost element styling (placeholder during drag) */
  .widget-ghost {
    opacity: 0.5;
    background: #f8f9fa;
    border: 2px dashed #e2e8f0;
  }

  /* Dragged element styling */
  .widget-drag {
    opacity: 0.9;
    transform: scale(1.02);
    box-shadow:
      0 20px 25px -5px rgb(0 0 0 / 0.1),
      0 8px 10px -6px rgb(0 0 0 / 0.1);
  }

  /* Smooth transitions for drag operations */
  .sortable-drag {
    transition: transform 200ms ease;
  }

  /* Handle hover effects */
  .widget-drag-handle:hover {
    background-color: #f3f4f6;
  }

  /* Animation for the widget transition */
  .v-move {
    transition: transform 0.3s ease;
  }
</style>
